import CustomTextField from '@/components/CustomTextField/CustomTextField';
import PolicyLogoFooter from '@/components/Footer/PolicyLogoFooter';
import Link from '@/components/Link/Link';
import { useInvoice } from '@/hooks/invoice/useInvoice';
import { FetchError } from '@/hooks/useApi';
import { useConfig } from '@/hooks/useConfig';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress, InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { referenceCodeSchema } from './schemas';

const Pay = () => {
  const { t } = useTranslation();
  const { urls, config } = useConfig();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [referenceChecked, setReferenceChecked] = useState<boolean>(false);
  const { invoice, setInvoiceReferenceCode, queryError, isLoading, postInvoice, resetInvoice } = useInvoice();
  const [referenceCode, setReferenceCode] = useState<string>('');

  const isValidReferenceCode = (referenceCode: string) => referenceCodeSchema.safeParse(referenceCode).success;

  const getQueryErrorMessage = (error: FetchError | null) => {
    if (!error?.status) return '';

    switch (error.status) {
      case 404:
        return t('page.pay.errors.reference-number-invalid')
      default:
        return t('errors.something-went-wrong')
    }
  };

  const validateReferenceCode = (referenceCode: string) => {
    const result = referenceCodeSchema.safeParse(referenceCode);
    const errorMessage = result?.error?.errors[0]?.message;

    setError(t(errorMessage ?? getQueryErrorMessage(queryError) ?? ''));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toLocaleUpperCase();

    // Enforce maximum length of 7 characters
    if (value.length > 7) {
      value = value.slice(0, 7);
    }

    setReferenceCode(value);
  };

  const handleResetPayment = async () => {
    await resetInvoice();
    setReferenceCode('');
  };

  const handleCheckReferenceCode = () => {
    if (isValidReferenceCode(referenceCode)) {
      setInvoiceReferenceCode(referenceCode);
      setReferenceChecked(true);
    } else {
      validateReferenceCode(referenceCode);
    }
  };

  const handlePostInvoice = async () => {
    await resetInvoice();

    postInvoice.mutate(
      {},
      {
        onSuccess: (result: string) => {
          setReferenceCode(result);
        },
        onError: (error: Error) => {
          console.error(error);
        }
      }
    );
  };

  useEffect(() => {
    if (invoice) {
      if (invoice?.status?.toLowerCase() === 'fullypaid') {
        return;
      }

      navigate(`/pay/${invoice.referenceCode}`);
    }

    if (queryError) {
      setError(getQueryErrorMessage(queryError));
    }
  }, [invoice, queryError]);

  return (
    <Stack justifyContent='center' alignItems='center' gap={4}>
      <Typography variant='body2' align='center'>
        {t('page.invoice.description')} {t('page.invoice.instructions')}
      </Typography>

      <CustomTextField
        autoFocus
        type='text'
        fullWidth
        disabled={invoice?.status?.toLowerCase() === 'fullypaid'}
        inputProps={{ maxLength: 7 }}
        label={t('common-phrases.reference')}
        placeholder={t('page.invoice.enter-reference-number')}
        value={referenceCode}
        onChange={handleChange}
        onKeyDown={(e) => e.key === 'Enter' && handleCheckReferenceCode()}
        error={!!error}
        helperText={error}
        slotProps={{
          input: {
            endAdornment:
              isValidReferenceCode(referenceCode) && !queryError ? (
                <InputAdornment position='end'>
                  <CheckCircleIcon color='success' fontSize='small' />
                </InputAdornment>
              ) : null
          }
        }}
      />

      {invoice?.status?.toLowerCase() === 'fullypaid' ? (
        <Stack gap={4} alignItems='center'>
          <Paper
            sx={{
              paddingY: 2,
              paddingX: 3
            }}
          >
            <Typography variant='body2'>
              {t('page.invoice.payment-already-processed')}{' '}
              <Link href={urls.myPages} target='_blank'>
                {t('common-phrases.my-pages')}
              </Link>
              .
            </Typography>
          </Paper>
          <Button fullWidth variant='outlined' color='primary' onClick={handleResetPayment}>
            {t('page.invoice.restart-payment')}
          </Button>
        </Stack>
      ) : (
        <Button
          fullWidth
          variant='contained'
          color='primary'
          onClick={handleCheckReferenceCode}
          disabled={referenceCode.length === 0 || isLoading}
        >
          {referenceChecked && isLoading ? (
            <Stack sx={{ p: 0.5 }}>
              <CircularProgress size={13} color='white' />
            </Stack>
          ) : (
            t('page.invoice.find-your-purchase')
          )}
        </Button>
      )}
      {(config.environment === 'DEV' || config.environment === 'TEST') && (
        <Button variant='outlined' onClick={handlePostInvoice}>
          Skapa Faktura
        </Button>
      )}
      <PolicyLogoFooter />
    </Stack>
  );
};

export default Pay;
